import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

// Formatea un número de orden como #fecha-numero (ej: #20251230-1234)
export function formatOrderFriendly(orderId: string, createdAt?: string): string {
  // If orderId is already in the format fecha-numero, just return it
  if (/^\d{8}-\w+/.test(orderId)) return `#${orderId}`;
  // Try to use createdAt or fallback to today
  let date = createdAt ? new Date(createdAt) : new Date();
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  // Use last 4-8 chars of orderId for uniqueness
  const num = orderId.slice(-8);
  return `#${y}${m}${d}-${num}`;
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Right off-canvas panels: shadow only when open (avoids edge bleed when translate-x-full). */
export function offcanvasSidebarPanelClass(isOpen: boolean, isClosing: boolean) {
  return cn(
    isOpen && !isClosing ? 'translate-x-0 shadow-2xl' : 'translate-x-full shadow-none',
    !isOpen && !isClosing && 'hidden'
  );
}

/**
 * Genera iniciales para avatares desde un nombre.
 * Si el nombre tiene 3+ palabras, toma inicial de 1ra, 2da y 3ra palabra (3 letras).
 * Si tiene 2 palabras, toma inicial de 1ra y 2da palabra (2 letras).
 * Si tiene 1 palabra, toma las primeras 3 letras.
 */
export function getAvatarInitials(name: string): string {
  if (!name || name.trim() === '') return '?';
  
  const trimmed = name.trim();
  const words = trimmed.split(/\s+/).filter(w => w.length > 0);
  
  if (words.length >= 3) {
    // Tomar inicial de primera, segunda y tercera palabra
    return (words[0][0] + words[1][0] + words[2][0]).toUpperCase();
  } else if (words.length === 2) {
    // Tomar inicial de primera y segunda palabra
    return (words[0][0] + words[1][0]).toUpperCase();
  } else if (words.length === 1) {
    // Tomar primeras 3 letras de la única palabra
    return words[0].slice(0, 3).toUpperCase();
  }
  
  return '?';
}

export const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Failed to copy: ', err);
    return false;
  }
};

// Helper para formatear números con separadores de miles (para inputs)
export const formatNumberInput = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '';
  
  const stringValue = String(value).replace(/[^0-9]/g, '');
  if (!stringValue) return '';

  const numberValue = parseInt(stringValue, 10);
  if (isNaN(numberValue)) return '';

  return new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numberValue);
};

// Helper para traducir la condición del producto
export const translateCondition = (condition: string | null): string => {
  if (!condition) return 'No especificada';
  switch (condition) {
    case 'new': return 'Nuevo';
    case 'like_new': return 'Como Nuevo';
    case 'used_good': return 'Usado (Buen estado)';
    case 'used_fair': return 'Usado (Estado regular)';
    case 'for_parts': return 'Para piezas';
    default: return condition;
  }
};

// Helper para traducir el tipo de producto
export const translateProductType = (type: string | null): string => {
  if (!type) return 'Desconocido';
  switch (type) {
    case 'book': return 'Libro';
    case 'electronics': return 'Electrónica';
    case 'vehicle': return 'Vehículo';
    case 'art': return 'Arte';
    case 'clothing': return 'Ropa';
    case 'propiedades': return 'Propiedades';
    case 'diversion': return 'Diversión';
    case 'other': return 'Almacén';
    default: return type.charAt(0).toUpperCase() + type.slice(1);
  }
};

// Helper para traducir valores específicos de detalles de vehículo
export const translateVehicleDetail = (key: string, value: any): string => {
  if (value === null || value === undefined) return '';
  
  if (typeof value === 'boolean') {
    return value ? 'Sí' : 'No';
  }

  if (typeof value !== 'string') {
    return String(value);
  }

  switch (key) {
    case 'fuel_type':
      switch (value) {
        case 'nafta': return 'Nafta';
        case 'diesel': return 'Diésel';
        case 'gnc': return 'GNC'; // Corregido a mayúsculas
        case 'electrico': return 'Eléctrico';
        case 'hibrido': return 'Híbrido';
        default: return value.charAt(0).toUpperCase() + value.slice(1);
      }
    case 'transmission':
      switch (value) {
        case 'manual': return 'Manual'; // Corregido a mayúscula inicial
        case 'automatica': return 'Automática'; // Corregido a mayúscula inicial
        default: return value.charAt(0).toUpperCase() + value.slice(1);
      }
    case 'wheel_condition':
    case 'engine_condition':
      switch (value) {
        case 'excelente': return 'Excelente'; // Corregido a mayúscula inicial
        case 'bueno': return 'Bueno'; // Corregido a mayúscula inicial
        case 'regular': return 'Regular'; // Corregido a mayúscula inicial
        case 'malo': return 'Malo'; // Corregido a mayúscula inicial
        default: return value.charAt(0).toUpperCase() + value.slice(1);
      }
    case 'oil_condition':
      switch (value) {
        case 'nuevo': return 'Nuevo'; // Corregido a mayúscula inicial
        case 'bueno': return 'Bueno'; // Corregido a mayúscula inicial
        case 'cambiar': return 'Necesita cambio';
        default: return value.charAt(0).toUpperCase() + value.slice(1);
      }
    default:
      // Para otros campos como 'color', 'brand', 'model', capitalizar la primera letra si es una cadena
      return value.charAt(0).toUpperCase() + value.slice(1);
  }
};

// Helper para formatear moneda a ARS ($) sin centavos
export const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return '$0';
  }
  // Format the number part first
  const numberPart = new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
  // Prepend the currency symbol directly
  return `$${numberPart}`;
};

// NEW: Helper para truncar el nombre de la ubicación hasta la primera coma
export const truncateLocationName = (locationName: string | null | undefined): string | null => {
  if (!locationName) return null;
  const parts = locationName.split(',');
  return parts[0].trim();
};

/**
 * Extrae el nombre de la ciudad de una dirección de OSM (display_name).
 * Heurística mejorada:
 * 1. Si el objeto tiene un campo cityName (vía Nominatim address), se usa.
 * 2. Si es un string, busca provincias como anclas y extrae el nivel anterior
 *    saltando barrios y niveles administrativos técnicos.
 */
export const extractCityName = (location: any): string | null => {
  if (!location) return null;

  // Prioridad 1: Si ya tenemos el campo cityName pre-extraído de Nominatim
  if (typeof location === 'object' && location.cityName) {
    return location.cityName;
  }

  const locationName = typeof location === 'string' ? location : location.displayName;
  if (!locationName) return null;

  const parts = locationName.split(',').map(p => p.trim());
  if (parts.length === 1) return parts[0];

  const PROVINCES = [
    'buenos aires', 'catamarca', 'chaco', 'chubut', 'córdoba', 'corrientes', 
    'entre ríos', 'formosa', 'jujuy', 'la pampa', 'la rioja', 'mendoza', 
    'misiones', 'neuquén', 'río negro', 'salta', 'san juan', 'san luis', 
    'santa cruz', 'santa fe', 'santiago del estero', 'tierra del fuego', 
    'tucumán', 'ciudad autónoma de buenos aires', 'caba'
  ];

  const ADMIN_MARKERS = ['departamento', 'municipio', 'partido de', 'comuna', 'pedanía', 'distrito', 'sección'];
  const NEIGHBORHOOD_MARKERS = ['barrio', 'b°', 'villa', 'v°', 'country', 'club', 'loteo', 'quinta', 'chacra'];

  // Filtrar país y códigos postales
  const filtered = parts.filter(p => {
    const low = p.toLowerCase();
    return low !== 'argentina' && !/^\d{4}$/.test(p) && !/^[A-Z]\d{4}[A-Z]{3}$/i.test(p);
  });

  if (filtered.length === 0) return parts[0];

  // Buscar índice de la provincia (ancla)
  let provIdx = -1;
  for (let i = filtered.length - 1; i >= 0; i--) {
    if (PROVINCES.includes(filtered[i].toLowerCase())) {
      provIdx = i;
      break;
    }
  }

  if (provIdx !== -1) {
    // Buscar el primer candidato limpio antes de la provincia
    for (let i = provIdx - 1; i >= 0; i--) {
      const part = filtered[i];
      const low = part.toLowerCase();

      // Ignorar si tiene números (calle) o marcadores de barrio/admin
      if (/\d/.test(part)) continue;
      if (ADMIN_MARKERS.some(m => low.includes(m))) continue;
      if (NEIGHBORHOOD_MARKERS.some(m => low.includes(m))) continue;

      // Si es igual a la provincia (ej: Salta city in Salta province), ok
      if (low === filtered[provIdx].toLowerCase()) return part;

      // Heurística específica para barrios que no tienen el prefijo "Barrio"
      // Si sólo queda una parte antes de la provincia y la provincia es una capital, 
      // y no es una ciudad "grande" conocida, podría ser un barrio.
      if (i === 0 && provIdx === 1) {
        const CAPITAL_EQUALS_PROVINCE = ['salta', 'córdoba', 'mendoza', 'santa fe', 'tucumán', 'jujuy', 'corrientes', 'san luis'];
        if (CAPITAL_EQUALS_PROVINCE.includes(filtered[provIdx].toLowerCase())) {
          // Si el nombre de la parte inicial no es el mismo que la provincia, 
          // probablemente es un barrio de la capital.
          return filtered[provIdx];
        }
      }

      return part;
    }
    return filtered[provIdx];
  }

  // Fallback si no hay provincia
  return filtered.length >= 2 ? filtered[filtered.length - 2] : filtered[0];
};

// Slugify helper: produce a url-friendly slug matching /^[a-z0-9_-]+$/
export const slugify = (value: string | undefined | null): string => {
  if (!value) return '';
  // Normalize and remove diacritics
  let s = value.normalize('NFKD').replace(/\p{Diacritic}/gu, '');
  s = s.toLowerCase();
  // Remove any non-alphanumeric characters (remove spaces, punctuation, hyphens)
  // and join words together so the slug is the company name concatenated.
  s = s.replace(/[^a-z0-9]+/g, '');
  return s;
};

// New: normalize to slug containing letters only (no numbers, no special chars), lowercase, joined, max length 30
export const normalizeLettersOnlySlug = (value: string | undefined | null): string => {
  if (!value) return '';
  let s = value.normalize('NFKD').replace(/\p{Diacritic}/gu, '');
  s = s.toLowerCase();
  // keep letters a-z only
  s = s.replace(/[^a-z]+/g, '');
  if (s.length > 30) s = s.slice(0, 30);
  return s;
};

// New: validate display name (letters and spaces only, max 30)
export const isValidDisplayName = (value: string | undefined | null): boolean => {
  if (!value) return false;
  const s = value.trim();
  if (s.length === 0 || s.length > 30) return false;
  // allow letters and spaces (including Spanish letters)
  return /^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/.test(s);
};

// New: validate that a value contains letters only (no numbers, no spaces, no special chars), allow diacritics
export const isLettersOnly = (value: string | undefined | null): boolean => {
  if (!value) return false;
  const s = value.trim();
  if (s.length === 0 || s.length > 30) return false;
  return /^[A-Za-zÀ-ÖØ-öø-ÿ]+$/.test(s);
};

// Blacklist of reserved names not allowed as usernames or store slugs
export const reservedNames = [
  'admin','administrator','administrador','root','system','sistema','sys',
  'login','signin','entrar','acceso','acceder','logout','signout','salir',
  'register','signup','registrarse','registro','auth','autenticacion',
  'password','contraseña','recover','recuperar','reset','resetear',
  'verify','verificar','verification','verificacion','code','codigo',
  'me','yo','my','mi','profile','perfil','account','cuenta','user','usuario',
  'settings','config','configuracion','ajustes','setup','install','instalar',
  'dashboard','panel','escritorio','notifications','notificaciones','messages','mensajes',
  'buscar','search','busqueda','query','explorar','explore','discover','descubrir',
  'noticias','news','novedades','novedad','blog','articulos','articles','posts',
  'tiendas','stores','shops','market','marketplace','mercado','tienda','shop',
  'categorias','categories','category','tags','etiquetas','topics','temas',
  'eventos','events','comunidad','community','forum','foro','groups','grupos',
  'videos','fotos','photos','images','imagenes','gallery','galeria','media',
  'mapa','maps','map','trending','tendencias','popular','clima','weather',
  'cart','carrito','basket','cesta','checkout','pago','pagar','pay','payment',
  'billing','facturacion','invoice','factura','order','orders','pedido','pedidos',
  'sales','ventas','deals','ofertas','oferta','promo','promociones','coupon','cupon',
  'discounts','descuentos','shipping','envio','envios','wallet','billetera',
  'customers','clientes','premium','gold','pro','enterprise','business','negocios',
  'help','ayuda','support','soporte','faq','preguntas','contact','contacto',
  'about','acerca','info','informacion','legal','terms','terminos','tos',
  'privacy','privacidad','policy','politica','feedback','claims','reclamos',
  'docs','documentation','documentacion','manual','guide','guia',
  'official','oficial','verified','verificado','staff','equipo','team',
  'moderator','moderador','mod','security','seguridad','owner','dueño',
  'founder','fundador','webmaster','host','hosting','mail','email','correo',
  'notificacion','alerta','alert','warning','aviso',
  'api','vone','vtwo','vthree','static','estatico','assets','scripts',
  'css','js','json','xml','database','sql','db','localhost','www','ww',
  'public','publico','private','privado','test','prueba','dev','developer',
  'desarrollo','git','webhook','proxy','cache','cdn','status','estado',
  'app','mobile','movil','download','descargar','upload','subir','internal',
  'home','inicio','landing','join','unirse','invite','invitacion','referral',
  'referidos','jobs','empleo','careers','carreras','press','prensa',
  'newsletter','subscribe','suscribirse','unsubscribe','cancelar'
];

// Check if given value (raw input) maps to a reserved name after normalization
export const isReservedName = (value: string | undefined | null): boolean => {
  if (!value) return false;
  const normalized = normalizeLettersOnlySlug(value);
  if (!normalized) return false;
  return reservedNames.includes(normalized);
};

/**
 * Calcula la distancia en kilómetros entre dos puntos geográficos (Haversine).
 */
export function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371; // Radio de la Tierra en km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Devuelve la dirección canónica completa tal como fue guardada.
 * No recorta ni reordena segmentos para mantener consistencia entre vistas.
 */
export const formatAddressForDisplay = (input?: string | null): string => {
  if (!input) return '';
  const raw = String(input).trim();
  if (!raw) return '';

  const parts = raw.split(',').map((part) => part.trim()).filter(Boolean);
  if (parts.length === 0) return raw;

  const normalized = parts.map((part, index) => {
    const isFourDigitPostal = /^\d{4}$/.test(part);
    const nextPart = String(parts[index + 1] || '').toLowerCase();
    const nearEnd = index >= parts.length - 3;
    const likelyPostalSegment = isFourDigitPostal && (nearEnd || nextPart.includes('argentina'));
    return likelyPostalSegment ? `A${part}` : part;
  });

  return normalized.join(', ');
};

// Convierte a "Sentence case" (Primera letra mayúscula, resto minúscula)
export const toSentenceCase = (text: string): string => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

// Mapea valores de product_type a etiquetas en español
export const translateProductCategoryLabel = (value: string | null): string => {
  if (!value) return 'Desconocido';
  const categoryMap: Record<string, string> = {
    'groceries': 'Almacén',
    'electronics': 'Electrónica',
    'clothing': 'Ropa',
    'book': 'Libro',
    'vehicle': 'Vehículo',
    'art': 'Arte',
    'diversion': 'Diversión',
    'propiedades': 'Propiedades',
    'tours': 'Excursiones',
    'clases_particulares': 'Clases particulares',
  };
  return categoryMap[value] || value;
};

// Mapea valores de groceries_subcategory a etiquetas en español
export const translateGroceriesSubcategoryLabel = (value: string | null): string => {
  if (!value) return '';
  const subcategoryMap: Record<string, string> = {
    'bebidas': 'Bebidas',
    'bebidas_con_alcohol': 'Bebidas con alcohol',
    'comestibles': 'Comestibles',
    'golosinas': 'Golosinas',
    'lacteos': 'Lácteos',
    'congelados': 'Congelados',
    'panaderia': 'Panadería',
    'limpieza': 'Limpieza',
    'cuidado_personal': 'Cuidado personal',
    'mascotas': 'Mascotas',
    'otros': 'Otros',
  };
  return subcategoryMap[value] || value;
};